import moment from 'moment'
import * as React from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Row, Col, Card, CardBody, Tooltip } from 'reactstrap'

import { getReportAverage, selectReportsStatus } from 'slices/reportsSlice'
import { selectSessionStatus } from 'slices/sessionSlice'
import { getTenant } from 'slices/tenantsSlice'

import { BadgeLabel, PeriodSelect } from 'components/common'
import { PERIOD_LIST, ColorTypes } from 'components/common/utils'

import useBusinessTime from 'hooks/useBusinessTime'

const productReport = (value: number | null): string => {
  return value !== null ? (Math.floor(value * 10) / 10).toFixed(1).toLocaleString() : '-'
}
const accuracyCount = (productivity: number | null, accuracy: number | null, hours: number): string => {
  if (!productivity || !accuracy) {
    return '-'
  }
  return Math.floor((productivity * accuracy * hours) / 100).toLocaleString()
}

type Props = {
  workspaceId?: number
}

const ReportAverage: React.FC<Props> = ({ workspaceId }) => {
  const [period, setPeriod] = React.useState<{ start: Date; end: Date }>({
    start: moment().subtract(PERIOD_LIST.DAYS_7, 'days').toDate(),
    end: moment().subtract(1, 'days').toDate(),
  })

  // 1日の営業時間 tenantHours を求める
  const { user } = useSelector(selectSessionStatus, shallowEqual)
  const tenantId = user.tenants[0].tenantId
  const dispatch = useDispatch()
  React.useEffect(() => {
    dispatch(getTenant(tenantId))
  }, [dispatch, tenantId])
  const { businessDuration } = useBusinessTime()

  React.useEffect(() => {
    if (!workspaceId) {
      return
    }
    const from = moment(period.start).format('YYYY-MM-DD')
    const to = moment(period.end).format('YYYY-MM-DD')
    dispatch(getReportAverage(workspaceId, from, to))
  }, [dispatch, workspaceId, period])

  const { reportAverages } = useSelector(selectReportsStatus, shallowEqual)

  const TooltipHintIcon: React.FC<{ text: string }> = ({ text }) => {
    const [tooltipOpen, setTooltipOpen] = React.useState(false)
    const toggle = () => setTooltipOpen(!tooltipOpen)
    const ref = React.useRef(null)

    return (
      <div>
        <i className="icf-hint font-middle" ref={ref} />
        <Tooltip target={ref} toggle={toggle} isOpen={tooltipOpen} placement="bottom">
          {text}
        </Tooltip>
      </div>
    )
  }

  return (
    <Card className="mb-3">
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="fw-bold">指定期間のサマリー</div>
          <PeriodSelect period={period} onChange={(start, end) => setPeriod({ start, end })} />
        </div>

        <Row md={3} className="mb-2">
          {reportAverages.map(reportAverage => (
            <Col key={`report-card-col-${reportAverage.scheduleTypeId}`} className="mb-2">
              <Card>
                <CardBody className="d-flex">
                  <BadgeLabel label={reportAverage.scheduleTypeName} color={reportAverage.scheduleTypeColor} />
                </CardBody>
                <CardBody className="pt-0">
                  <div className="font-x-small text-muted d-flex">
                    生産性
                    <TooltipHintIcon text="指定された期間の実績の合計を作業計画で入力された予定の合計で割った数値" />
                  </div>

                  <span className="font-large fw-bold">
                    {reportAverage.hourlyAvgProductivity !== null
                      ? Math.floor(reportAverage.hourlyAvgProductivity).toLocaleString()
                      : '-'}
                    {reportAverage.unit}
                  </span>
                  <small> /1時間</small>
                </CardBody>
                <hr className="m-0" color={ColorTypes.Silver} />
                <CardBody>
                  <div className="font-x-small text-muted d-flex">
                    平均計画変更率
                    <TooltipHintIcon text="アーカイブされた作業計画と最終的な作業計画の変更割合（=計画変更率）の指定された期間の平均" />
                  </div>
                  <span className="font-large fw-bold">&#177;{productReport(reportAverage.dailyAvgPlanAccuracy)}%</span>
                  <small>/1日</small>
                  <div className="font-x-small text-muted">
                    &#177;
                    {accuracyCount(
                      reportAverage.hourlyAvgProductivity,
                      reportAverage.dailyAvgPlanAccuracy,
                      businessDuration / 4
                    )}
                    {reportAverage.unit}
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  )
}

export default ReportAverage
